import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppSidebar from '../../components/AppSidebar';
import AppHeader from '../../components/AppHeader';
import PageHeader from '../../components/PageHeader';
import TasksService from '../../services/TasksService';
import UsersService from '../../services/UsersService';
import ConfigurationsService from '../../services/ConfigurationsServices';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import TagsInput from "../../components/TagsInput";
import { BsTagsFill } from "react-icons/bs";
import { useAuth } from '../../contexts/AuthContext';

const EditTask = () => {
  const { id } = useParams();
  const { register, handleSubmit, formState: { errors }, control, getValues, watch, setValue, reset } = useForm();
  const [assigneeList, setAssigneeList] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const startDate = watch('start_date')
  const options = useMemo(() => [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ], []);

  const fetchData = useCallback(async () => {
    try {
      const taskResponse = await TasksService.getTask(id);
      const assigneeResponse = await UsersService.list();
      const typeResponse = await ConfigurationsService.getAllModules();
      const taskData = taskResponse.data.data;
      const assignees = assigneeResponse.data.data;
      const modules = typeResponse.data.data;

      if (taskData) {
        setAssigneeList(assignees.map(assignee => ({
          value: assignee.id,
          label: assignee.name + '-' + assignee.email,
        })));

        setTypeOptions(modules.map((module, index) => ({
          value: index.toString(),
          label: module.label
        })));

        const tags = formattedTags(taskData?.tags);
        setTags(tags);

        reset({
          ...taskData,
          priority: options.find(option => option.value === taskData.priority) || options[1],
          assignees: taskData.users.map(user => ({
            value: user.id,
            label: user.name
          })),
          start_date: taskData.start_date?.slice(0, 10) || '',
          due_date: taskData.due_date?.slice(0, 10) || '',
          is_public: taskData.is_public ?? false,
          is_billable: taskData.is_billable ?? false,
          type: taskData.type ? { label: taskData.type, value: taskData.type } : null,
          attachment: taskData.attachment || null
        });
      } else {
        toast.error('Task data is empty or undefined');
      }
    } catch (error) {
      console.error('Error fetching data', error);
      toast.error('Error fetching data');
    }
  }, [id, reset, options]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formattedTags = (tags) => {
    if (typeof tags === "string") {
      try {
        tags = JSON.parse(tags);
      } catch (error) {
        console.error("Error parsing tags:", error);
        tags = [];
      }
    }

    const timestamp = new Date().getTime();
    return tags?.map((tag, index) => ({
      id: `${timestamp}-${index}`,
      text: tag,
    })) || [];
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const requestBody = {
      data: {
        customer_id: user?.id,
        type: data?.type?.label,
        is_public: data?.is_public,
        is_billable: data?.is_billable,
        subject: data?.subject,
        start_date: data?.start_date,
        due_date: data?.due_date,
        assignees: data?.assignees.map(assignee => assignee?.value),
        tags: tags?.map(tag => tag?.text),
        description: data?.description,
        priority: data?.priority?.value
      }
    };

    try {
      const response = await TasksService.updateTask(id, requestBody);
      if (response.status === 200) {
        toast.success('Task updated successfully');
      }
    } catch (error) {
      console.error('Error updating task', error);
      toast.error(error?.response?.data?.message || error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppSidebar />
      <div className="main-content">
        <AppHeader />
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-12">
              <PageHeader title="Edit Task" button_label1="Back"
                link_to1="/tasks" className1="btn btn-primary btn-sm" />
              <div className="card p-4">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row">
                    {/* Form Controls */}
                    <div className="col-md-6">
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          {...register("is_public")}
                          onChange={(e) => setValue("is_public", e.target.checked)}
                        />
                        <label className="form-check-label">Public</label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          {...register("is_billable")}
                          onChange={(e) => setValue("is_billable", e.target.checked)}
                        />
                        <label className="form-check-label">Billable</label>
                      </div>
                    </div>
                    <hr className="my-3" />
                    {/* Task Form Fields */}
                    <div className="col-12 col-md-12">
                      <div className="form-group pb-4">
                        <label className="form-label">Subject</label>
                        <input
                          type="text"
                          {...register('subject', {
                            required: "Subject is required",
                            minLength: {
                              value: 2,
                              message: "Product Name must be longer than or equal to 2 characters"
                            }
                          })}
                          className={`form-control ${errors?.subject ? 'is-invalid' : ''}`}
                        />
                        {errors?.subject && <div className="invalid-feedback">{errors?.subject?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Start Date</label>
                        <input
                          type="date"
                          {...register('start_date', {
                            required: "Start date is required",
                            validate: (value) => {
                              const dueDate = getValues('due_date');
                              if (new Date(value) > new Date(dueDate)) {
                                return 'Start date cannot be later than due date';
                              }
                            }
                          })}
                          className={`form-control ${errors?.start_date ? 'is-invalid' : ''}`}
                          min={startDate}
                        />
                        {errors?.start_date && <div className="invalid-feedback">{errors?.start_date?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Due Date</label>
                        <input
                          type="date"
                          {...register('due_date', { required: "Due date is required" })}
                          className={`form-control ${errors?.due_date ? 'is-invalid' : ''}`}
                          min={startDate}
                        />
                        {errors?.due_date && <div className="invalid-feedback">{errors?.due_date?.message}</div>}
                      </div>
                    </div>
                    {/* Priority, Assignees, Type */}
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Priority</label>
                        <Controller
                          name="priority"
                          control={control}
                          render={({ field }) => <Select {...field} options={options} />}
                        />
                        {errors?.priority && <div className="invalid-feedback">{errors?.priority?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Assignees</label>
                        <Controller
                          name="assignees"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              isMulti
                              options={assigneeList}
                              closeMenuOnSelect={false}
                            />
                          )}
                        />
                        {errors?.assignees && <div className="invalid-feedback">{errors?.assignees?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Type</label>
                        <Controller
                          name="type"
                          control={control}
                          render={({ field }) => (
                            <Select {...field} options={typeOptions} placeholder="Select Type" />
                          )}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <BsTagsFill /> <label className="mb-1 form-label">Tags</label>
                        <TagsInput tags={tags} setTags={setTags} />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Description</label>
                        <textarea
                          {...register('description')}
                          className="form-control"
                          rows={3}
                        />
                      </div>
                    </div>

                  </div>
                  <div className="modal-footer">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
                <ToastContainer />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditTask;
